import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Loader, DynIcon, RARITY } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const ICON_CHOICES = ["Mic", "Search", "Palette", "Anchor", "Zap", "Sparkles", "Smile", "Eye", "BookOpen", "Trophy", "Flame", "Award", "Star", "Medal", "Rocket"];
const empty = { name: "", description: "", icon: "Award", color: "#662D91", rarity: "common", points: 100, week: 1, criteria: "", active: true };

export default function BadgeBuilder() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["badges"], queryFn: async () => (await api.get("/badges")).data });

  const save = async () => {
    if (!form.name) return toast.error("Badge name required");
    const payload = { ...form, points: Number(form.points), week: Number(form.week) };
    try {
      if (editId) await api.put(`/badges/${editId}`, payload); else await api.post("/badges", payload);
      toast.success("Badge saved!"); setOpen(false); setForm(empty); setEditId(null);
      qc.invalidateQueries({ queryKey: ["badges"] });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const edit = (b) => { setForm(b); setEditId(b.id); setOpen(true); };
  const del = async (id) => { await api.delete(`/badges/${id}`); qc.invalidateQueries({ queryKey: ["badges"] }); toast.success("Deleted"); };

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-5" data-testid="badge-builder">
      <div className="flex items-center justify-between">
        <h2 className="font-head text-3xl text-csh-blue">Badge Builder 🏅</h2>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditId(null); } }}>
          <DialogTrigger asChild><Button className="rounded-full gradient-purple text-white" data-testid="add-badge-btn"><Icons.Plus size={18} /> New Badge</Button></DialogTrigger>
          <DialogContent className="rounded-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-head text-2xl text-csh-blue">{editId ? "Edit" : "Create"} Badge</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="flex justify-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full" style={{ backgroundColor: form.color + "22", border: `3px solid ${form.color}` }}>
                  <DynIcon name={form.icon} size={36} color={form.color} />
                </div>
              </div>
              <Input placeholder="Badge name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-2 py-6" data-testid="badge-name" />
              <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl border-2" />
              <Input placeholder="Unlock criteria" value={form.criteria} onChange={(e) => setForm({ ...form, criteria: e.target.value })} className="rounded-xl border-2 py-6" />
              <div className="flex flex-wrap gap-2">
                {ICON_CHOICES.map((ic) => (
                  <button key={ic} onClick={() => setForm({ ...form, icon: ic })} className={`flex h-10 w-10 items-center justify-center rounded-xl border-2 ${form.icon === ic ? "border-csh-purple bg-csh-purple/10" : "border-black/10"}`}>
                    <DynIcon name={ic} size={18} color="#662D91" />
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold">Color</label>
                  <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="rounded-xl border-2 h-12" />
                </div>
                <div>
                  <label className="text-xs font-bold">Rarity</label>
                  <Select value={form.rarity} onValueChange={(v) => setForm({ ...form, rarity: v })}>
                    <SelectTrigger className="rounded-xl border-2 py-6"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.keys(RARITY).map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input type="number" placeholder="Points" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className="rounded-xl border-2 py-6" />
                <Input type="number" placeholder="Week" value={form.week} onChange={(e) => setForm({ ...form, week: e.target.value })} className="rounded-xl border-2 py-6" />
              </div>
              <Button onClick={save} className="w-full rounded-full gradient-orange py-6 font-bold text-white" data-testid="save-badge-btn">Save Badge</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {(data || []).map((b) => {
          const r = RARITY[b.rarity] || RARITY.common;
          return (
            <div key={b.id} className="relative flex flex-col items-center rounded-3xl bg-white p-5 text-center shadow-sm border-2" style={{ borderColor: r.ring }} data-testid={`bb-${b.id}`}>
              <span className="absolute right-2 top-2 rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ backgroundColor: r.bg, color: r.ring }}>{r.label}</span>
              <div className="my-2 flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: b.color + "22", border: `3px solid ${b.color}` }}>
                <DynIcon name={b.icon} size={28} color={b.color} />
              </div>
              <h3 className="font-head text-base text-csh-blue leading-tight">{b.name}</h3>
              <p className="text-[11px] text-muted-foreground">+{b.points} XP · Wk {b.week}</p>
              <div className="mt-2 flex gap-1">
                <button onClick={() => edit(b)} className="rounded-lg p-1.5 hover:bg-black/5"><Icons.Pencil size={14} className="text-csh-purple" /></button>
                <button onClick={() => del(b.id)} className="rounded-lg p-1.5 hover:bg-black/5"><Icons.Trash2 size={14} className="text-red-500" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
